import {
  Box,
  Divider,
  IconButton,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import {
  ScheduleMode,
  type BackupSchedule,
  type BackupPolicyConfig,
} from '@repo/api-gw-sdk';
import { useEffect, useState } from 'react';

import { BackupPolicyTypeTag } from '@/app/(dashboard)/settings/backup-policies/backupPolicyTypeTag';
import {
  getFrequencyByWindow,
  getFrequencyDisplayName,
} from '@/app/(dashboard)/settings/frequency';
import type { BackupVault } from '@/data/vaults/backupVault';
import { fromDaysToBiggestAsPossible } from '@/utils/dateTime';

import { SnapshotIndicator } from '../snapshots/SnapshotIndicator';
import { Tag } from '../tag';
import BackupVaultCard from '../vaults/backupVaultCard';

const SelectableIconBtn = styled(IconButton)<{ selected?: boolean }>(
  ({ selected }) => ({
    border: selected ? `1px solid var(--mui-palette-text-primary)` : 'none',
    padding: '4px',
    margin: selected ? '0 4px' : 'none',
    borderRadius: '50%',
  })
);

export default function BackupPolicyPopperCard({
  policy,
  vaults,
}: {
  policy: BackupPolicyConfig;
  vaults: BackupVault[];
}) {
  const [selectedSchedule, setSelectedSchedule] =
    useState<BackupSchedule | null>(null);

  useEffect(() => {
    if (policy.schedules) {
      if (!selectedSchedule && policy.schedules.length > 0) {
        setSelectedSchedule(policy.schedules[0]);
      }
    }
  }, [policy.schedules, selectedSchedule, vaults]);

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      sx={{
        backgroundColor: 'var(--mui-palette-background-paper)',
        boxShadow: '0px 10px 30px 0px rgba(0, 0, 0, 0.10)',
        width: '368px',
        padding: '24px',
        paddingBottom: '12px',
        borderRadius: '16px',
      }}
    >
      <Stack
        className='mb-[32px]'
        gap='12px'
        direction='row'
        alignItems='center'
      >
        <Typography variant='h6'>{policy.name}</Typography>
        <BackupPolicyTypeTag policyType={policy.policyType} />
        {!policy.enabled && <Tag content='Disabled' disabled />}
      </Stack>
      <Stack gap='16px'>
        <Stack direction='row' alignItems='start' gap='40px'>
          <Typography className='min-w-[86px]' variant='subtitle1'>
            Frequency
          </Typography>
          <Stack gap='8px'>
            <Typography>
              {selectedSchedule &&
                getFrequencyByWindow(selectedSchedule.windows)?.text}
            </Typography>
            {policy.scheduleMode === ScheduleMode.SCHEDULE_MODE_ADVANCED &&
              selectedSchedule && (
                <Typography>
                  {getFrequencyDisplayName(selectedSchedule.windows)}
                </Typography>
              )}
          </Stack>
        </Stack>
        <Divider sx={{ borderStyle: 'dashed' }} />
        <Stack direction='row' alignItems='center' gap='40px'>
          <Typography className='min-w-[86px]' variant='subtitle1'>
            Retention
          </Typography>
          <Typography>
            {selectedSchedule?.backupRetention &&
              formatRetention(selectedSchedule.backupRetention)}
          </Typography>
        </Stack>
        <Divider sx={{ borderStyle: 'dashed' }} />
        <Typography className='min-w-[86px]' variant='subtitle1'>
          Vault
        </Typography>
      </Stack>

      <div className='mt-[16px]'>
        {selectedSchedule ? (
          <BackupVaultCard
            editable={false}
            data={vaults.find((v) => v.id === selectedSchedule.vaultId)}
          />
        ) : (
          <BackupVaultCard loading={true} editable={false} />
        )}
      </div>
      {policy?.schedules.length > 1 ? (
        <>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            sx={{
              margin: '0 -24px',
              marginTop: '24px',
              padding: '12px 24px',
              paddingBottom: 0,
              borderTop: '1px solid var(--mui-palette-divider)',
            }}
          >
            <Typography variant='body1'>
              {`${
                selectedSchedule
                  ? (policy?.schedules.findIndex(
                      (p) => p.id === selectedSchedule?.id
                    ) ?? 0) + 1
                  : 1
              } of ${policy?.schedules?.length ?? 0}`}
            </Typography>
            <Stack direction='row' alignItems='center' gap='4px'>
              <IconButton
                size='small'
                onClick={() => {
                  const currentIndex = selectedSchedule
                    ? policy.schedules.findIndex(
                        (s) => s.id === selectedSchedule.id
                      )
                    : 0;
                  setSelectedSchedule(
                    policy.schedules[
                      (currentIndex - 1 + policy.schedules.length) %
                        policy.schedules.length
                    ]
                  );
                }}
              >
                <i className='material-symbols-chevron-left-rounded' />
              </IconButton>
              {policy.schedules.map((schedule) => {
                const vault = vaults.find((v) => v.id === schedule.vaultId);
                const selected = selectedSchedule?.id === schedule.id;
                return (
                  <SelectableIconBtn
                    size='small'
                    key={schedule.id}
                    selected={selected}
                    onClick={() => {
                      if (!selected) {
                        setSelectedSchedule(schedule);
                      }
                    }}
                  >
                    <SnapshotIndicator
                      backgroundColor={vault?.backgroundColor || null}
                    />
                  </SelectableIconBtn>
                );
              })}
              <IconButton
                size='small'
                onClick={() => {
                  const currentIndex = selectedSchedule
                    ? policy.schedules.findIndex(
                        (s) => s.id === selectedSchedule.id
                      )
                    : 0;
                  setSelectedSchedule(
                    policy.schedules[
                      (currentIndex + 1) % policy.schedules.length
                    ]
                  );
                }}
              >
                <i className='material-symbols-chevron-right-rounded' />
              </IconButton>
            </Stack>
          </Stack>
        </>
      ) : (
        <div className='mb-[12px]'></div>
      )}
    </Box>
  );
}

const formatRetention = (backupRetention: number) => {
  const formattedRetention = fromDaysToBiggestAsPossible(backupRetention);
  return `${formattedRetention.value} ${formattedRetention.unit}${
    formattedRetention.unit !== 'days' ? ` (${backupRetention} days)` : ''
  }`;
};
